import { ApiClient } from './client.js';

export type ActionType = 'audio.enable' | 'audio.disable' | 'wake.enable' | 'wake.disable' | 'session.stop' | 'camera.capture';

export type ActionState = 'queued' | 'running' | 'succeeded' | 'failed' | 'expired';

export interface DeviceAction {
  id: string;
  type: ActionType;
  state: ActionState;
  expiresAt: number;
  resultCode: string | null;
  imageAvailable: boolean;
}

export interface DeviceCapabilities {
  audio: boolean;
  wake: boolean;
  camera: boolean;
}

export interface DeviceReport {
  audioEnabled: boolean;
  wakeEnabled: boolean;
  capturing: boolean;
  speaking: boolean;
  capabilities: DeviceCapabilities;
}

export interface DeviceControl {
  supported: boolean;
  reportedAt: number | null;
  report: DeviceReport | null;
  activeAction: DeviceAction | null;
}

export interface DeviceActionRequest {
  type: ActionType;
  consent: boolean;
}

export interface DeviceActionResponse {
  action: DeviceAction;
}

export interface DeviceCameraImage {
  mimeType: 'image/jpeg';
  imageBase64: string;
}

export class DeviceControlsApi {
  constructor(private readonly client: ApiClient) {}

  getControl(deviceId: string, signal?: AbortSignal): Promise<DeviceControl> {
    return this.client.request('GET', `/api/devices/${encodeURIComponent(deviceId)}/control`, undefined, { signal, op: 'devices.control' });
  }

  create(deviceId: string, body: DeviceActionRequest, signal?: AbortSignal): Promise<DeviceActionResponse> {
    return this.client.request('POST', `/api/devices/${encodeURIComponent(deviceId)}/actions`, body, { signal, op: 'devices.actions.create' });
  }

  get(deviceId: string, actionId: string, signal?: AbortSignal): Promise<DeviceActionResponse> {
    return this.client.request('GET', `/api/devices/${encodeURIComponent(deviceId)}/actions/${encodeURIComponent(actionId)}`, undefined, { signal, op: 'devices.actions.get' });
  }

  image(deviceId: string, actionId: string, signal?: AbortSignal): Promise<DeviceCameraImage> {
    return this.client.request('GET', `/api/devices/${encodeURIComponent(deviceId)}/actions/${encodeURIComponent(actionId)}/image`, undefined, { signal, op: 'devices.actions.image' });
  }
}

export function createDeviceControlsApi(api: ApiClient): DeviceControlsApi {
  return new DeviceControlsApi(api);
}
